import {KeyboardKey} from '@github-ui/keyboard-key'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {Box, Button, Checkbox, FormControl, Text} from '@primer/react'
import type {DialogProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {DragAndDropResources} from '../utils/strings'
import type {DragAndDropDirection} from '../utils/types'
import clsx from 'clsx'

import styles from './KeyboardSpecificInstructionsModal.module.css'

type KeyboardSpecificInstructionsModalProps = {
  /**
   * Whether the modal is open
   */
  isOpen: boolean
  /**
   * Callback called when the modal is closed
   */
  onClose: () => void
  /**
   * The direction of the list
   */
  direction: DragAndDropDirection
}

type KeyboardSpecificInstructionsFooterProps = Pick<KeyboardSpecificInstructionsModalProps, 'onClose'> & DialogProps

/*
 * Key used to store the user preference to stop showing the keyboard instructions modal
 */
export const HideKeyboardSpecificInstructionsModalKey = 'hideKeyboardSpecificInstructions'

const KeyboardSpecificInstructionsFooter = ({onClose}: KeyboardSpecificInstructionsFooterProps) => {
  const [doNotShowAgain, setDoNotShowAgain] = useLocalStorage(HideKeyboardSpecificInstructionsModalKey, false)
  return (
    <Box className={clsx(styles.footerContainer)}>
      <Box className={clsx(styles.footerForm)}>
        <FormControl>
          <Checkbox checked={doNotShowAgain} onChange={() => setDoNotShowAgain(!doNotShowAgain)} />
          <FormControl.Label>{`Don't show this again`}</FormControl.Label>
        </FormControl>
      </Box>
      <Box className={clsx(styles.footerButton)}>
        <Button onClick={onClose}>Close</Button>
      </Box>
    </Box>
  )
}

/* Modal component that displays keyboard instructions to perform drag and drop actions.
 * Using the Primer Dialog component.
 *
 * @param props KeyboardSpecificInstructionsModalProps
 */
export const KeyboardSpecificInstructionsModal = ({
  isOpen,
  onClose,
  direction,
}: KeyboardSpecificInstructionsModalProps) => {
  if (!isOpen) return null

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.code === 'Escape') {
      onClose()
    }
  }

  return (
    <Box onKeyDown={onKeyDown}>
      <Dialog
        title="How to move objects via keyboard"
        subtitle="This navigation is only available when move mode is activated."
        onClose={onClose}
        renderFooter={() => (
          <Dialog.Footer className="d-flex">
            <KeyboardSpecificInstructionsFooter onClose={onClose} />
          </Dialog.Footer>
        )}
      >
        <Box as="table" className={clsx(styles.instructionContainer)}>
          <Box className="sr-only" as="thead">
            <Box as="tr">
              <Text as="th" className="text=bold">
                Action
              </Text>
              <Text as="th" className="text=bold">
                Keyboard Shortcut
              </Text>
            </Box>
          </Box>
          <Box as="tbody">
            <Box as="tr" className={clsx(styles.instruction)}>
              <Text as="td">{DragAndDropResources.cancelDrag}</Text>
              <Box as="td">
                <KeyboardKey keys="esc" />
              </Box>
            </Box>
            <Box as="tr" className={clsx(styles.instruction)}>
              <Text as="td">{DragAndDropResources.movePosition}</Text>
              <Box as="td">
                <KeyboardKey keys={direction === 'vertical' ? 'up' : 'left'} />
                <Text> / </Text>
                <KeyboardKey keys={direction === 'vertical' ? 'down' : 'right'} />
              </Box>
            </Box>
            <Box as="tr" className={clsx(styles.instruction, 'border-bottom-0')}>
              <Text as="td">{DragAndDropResources.endDrag}</Text>
              <Box as="td">
                <KeyboardKey keys="enter" />
                <Text> or </Text>
                <KeyboardKey keys="space" />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  )
}

try{ KeyboardSpecificInstructionsFooter.displayName ||= 'KeyboardSpecificInstructionsFooter' } catch {}
try{ KeyboardSpecificInstructionsModal.displayName ||= 'KeyboardSpecificInstructionsModal' } catch {}